import type { ApiResponse, ApiErr } from '@timesheet/shared';
import { getToken, clearAllAuth } from './storage';

const API_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:8787';

interface ApiOptions extends Omit<RequestInit, 'body'> {
  /** JSON body — will be stringified. Use `rawBody` for non-JSON. */
  json?: unknown;
  /** Bypass auto-attaching the Authorization header (e.g. for /auth/login). */
  skipAuth?: boolean;
}

/**
 * Typed fetch wrapper.
 *
 *   const res = await api<LoginResponse>('/auth/login', { method: 'POST', json: { username, password } });
 *   if (res.ok) {
 *     // res.data is fully typed as LoginResponse
 *   } else {
 *     // res.error.code / message / fields
 *   }
 *
 * - Auto-attaches `Authorization: Bearer <token>` if a token is stored
 * - On 401 (`AUTH_INVALID`) auto-clears stored auth so the auth store can react
 * - Network errors are converted to a synthetic ApiErr (never throws)
 */
export async function api<T>(path: string, opts: ApiOptions = {}): Promise<ApiResponse<T>> {
  const { json, skipAuth, headers: extraHeaders, ...rest } = opts;

  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...(extraHeaders as Record<string, string> | undefined),
  };

  let body: BodyInit | undefined;
  if (json !== undefined) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(json);
  }

  if (!skipAuth) {
    const token = getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }

  let res: Response;
  try {
    res = await fetch(`${API_URL}${path}`, { ...rest, headers, body });
  } catch (networkErr) {
    console.error('[api] network error', networkErr);
    return {
      ok: false,
      error: {
        code: 'NETWORK',
        message: 'Cannot reach API. Check your connection.',
      },
    };
  }

  // Parse JSON regardless of status code — server uses ApiResponse<T> shape for both.
  let payload: ApiResponse<T>;
  try {
    payload = (await res.json()) as ApiResponse<T>;
  } catch {
    return {
      ok: false,
      error: {
        code: 'BAD_RESPONSE',
        message: `Server returned non-JSON response (HTTP ${res.status})`,
      },
    };
  }

  // Auto-logout on AUTH_INVALID — the auth store also subscribes to storage events
  // but explicit clearing here makes the flow tighter.
  if (!payload.ok && (payload as ApiErr).error.code === 'AUTH_INVALID') {
    clearAllAuth();
  }

  return payload;
}

/** Convenience: throw on error, return data. Use with TanStack Query mutationFn / queryFn. */
export async function apiOrThrow<T>(path: string, opts: ApiOptions = {}): Promise<T> {
  const res = await api<T>(path, opts);
  if (!res.ok) {
    const err = new Error(res.error.message);
    (err as Error & { code?: string; fields?: Record<string, string> }).code = res.error.code;
    (err as Error & { code?: string; fields?: Record<string, string> }).fields = res.error.fields;
    throw err;
  }
  return res.data;
}
